/**
 * Die Naht zu `lib/parsers/` - portiert wörtlich aus dem Task-Brief (Plan
 * `docs/superpowers/plans/2026-08-09-phase-06-react-spa.md`, Task 19). Die Parser selbst sind NICHT
 * Teil dieser Phase (eigener Plan, sie haengen am PI-Korpus und an PDF.js, Tesseract und SheetJS).
 * Diese Datei legt fest, WAS sie liefern muessen, und laedt sie erst beim ersten Gebrauch -
 * "OCR- und Excel-Module werden per Code-Splitting lazy geladen, erst wenn der Nutzer ein Dokument
 * hochlaedt" (Spec, Fremdbibliotheken).
 *
 * ================= Was diese Datei NICHT MEHR ist (Task 3, Plan 2026-08-17-bestellexcel-anlagemaske) =
 *
 * Bis zu diesem Task stand hier, dass `./index.ts` NICHT existiert und `ladeParser()` deshalb ausserhalb
 * von Tests immer einen Modul-nicht-gefunden-Fehler wirft. Das gilt nicht mehr: `./index.ts` liegt jetzt
 * im Baum (siehe dort) und verdrahtet den Excel-Anteil ECHT (`excelAuslesen`/`excelEntwurfAuslesen` ueber
 * `createOrderExcelImportDraft`, `./auslesen.ts`). `piAuslesen`/`zollAuslesen` sind WEITERHIN nicht
 * portiert - Tasks 1-7 des Parser-Plans (PI-Korpus, PDF.js, Tesseract) liegen nicht in diesem Baum -,
 * werfen aber seit diesem Task einen eigenen, benannten `ParserNichtPortiertFehler` statt eines rohen
 * Modul-nicht-gefunden-Fehlers (siehe `index.ts`, Kopfkommentar).
 *
 * ================= Zuordnung Dokumenttyp -> Methode (eigene Ergaenzung, siehe Task-Bericht) =====
 *
 * Der Brief nennt nur die drei Methoden; welcher Dokumenttyp welche Methode ruft, ergibt sich aus
 * `config/documents.ts`:
 *  - `type === 'pi'`                                        -> `piAuslesen`
 *  - `type === 'bestellexcel'`                               -> `excelAuslesen`
 *  - `DOCUMENT_OCR_TYPES[type]?.kind` ist `'customs'`/`'freight'` -> `zollAuslesen`
 *  - `DOCUMENT_OCR_TYPES[type]?.kind === 'ci'` (Sammelrechnung)   -> KEINE Methode der Naht passt;
 *    im Bestand liest `extractBusinessDocumentFromDoc` (index.html:12572) auch fuer `ci` denselben
 *    generischen Text-/OCR-Pfad wie Zoll- und Frachtdokumente - die Naht des Briefs bildet dafuer aber
 *    keine eigene Methode ab. Gemeldet, nicht erfunden: `DokumenteTab` ruft fuer `ci` keine
 *    Parser-Methode (siehe Task-Bericht, Abschnitt "Parser-Zuordnung").
 */
import type { OrderItem } from "../types.ts";
// `import type`, nicht `import`: `excel.ts` importiert seinerseits (`import type`) `ExcelZeile` aus
// DIESER Datei - ein Wertimport hier ergaebe einen Ring zwischen den beiden Modulen. Re-exportiert
// unten, damit ein Verbraucher der Naht `ExcelEntwurf` nicht am Parser-Modul vorbei importieren muss
// (Teil 1b, Task 3).
import type { ExcelEntwurf } from "./excel.ts";

export type { ExcelEntwurf };

/** Eine ausgelesene PI-Position - Form wie `piImport.fields.items[]` (index.html:10439 ff.). */
export interface PiPosition extends OrderItem {
  name?: unknown;
  sku?: unknown;
  qty?: unknown;
  price?: unknown;
  foc?: unknown;
  manual?: unknown;
}

/** Eine ausgelesene Bestellexcel-Zeile - Form wie `orderExcelImport.items[]` (index.html:12857). */
export interface ExcelZeile {
  name?: string;
  sku?: string;
  ean?: string;
  qty?: string;
  price?: string;
}

/** Eine ausgelesene Zoll-/Fracht-Position - Form wie `documentOcrDraft` liest sie fuer die Verteilung (index.html:12266 ff., `documentItemAllocations`-Eingabe). */
export interface ZollPosition {
  name?: string;
  sku?: string;
  qty?: unknown;
  price?: unknown;
  sourceAmount?: unknown;
}

/**
 * Der Brief, woertlich: die drei Methoden, die ein Parser liefern muss. `felder`/`positionen`
 * entsprechen `parseBusinessDocumentText`/`repairStoredPiImport`s `{ fields, items }` bzw.
 * `parseOrderExcelMatrix`s `{ items }` (index.html:12829-12870), hier auf die Naht verallgemeinert.
 */
export interface DokumentParser {
  piAuslesen(datei: File): Promise<{ felder: Record<string, unknown>; positionen: PiPosition[] }>;
  /**
   * RULING R16 (Fixwelle nach der Endabnahme, F1): seit `DokumenteTab` auf `excelEntwurfAuslesen`
   * umgestellt ist (derselbe volle Satz wie die Anlagemaske schreibt), hat diese Methode HEUTE
   * keinen Aufrufer mehr im Baum. Sie bleibt trotzdem in der Naht: sie ist Teil der Vertragsflaeche
   * fuer Parser, die es noch nicht gibt - `piAuslesen`/`zollAuslesen` haben ebenfalls keine echte
   * Umsetzung (siehe `index.ts`), und Task 9 des Parser-Plans
   * (`docs/superpowers/plans/2026-08-11-lib-parsers.md`) schreibt `excelAuslesen` ausdruecklich vor.
   * Fuer den benutzten Weg siehe `excelEntwurfAuslesen` unten.
   */
  excelAuslesen(datei: File): Promise<ExcelZeile[]>;
  zollAuslesen(datei: File): Promise<{ abgaben: number; positionen: ZollPosition[] }>;
  /**
   * Der volle Entwurf, wie `createOrderExcelImportDraft` ihn liefert - mit Warnungen, Blattname
   * und Zeilenzahl. Der BENUTZTE Weg fuer beide Verbraucher: die Anlagemaske
   * (`index.html:8060-8088`) und, seit F1 der Fixwelle nach der Endabnahme, `DokumenteTab` -
   * `excelAuslesen` oben liefert nur `items` und hat seither keinen eigenen Aufrufer mehr (siehe dort).
   */
  excelEntwurfAuslesen(datei: File): Promise<ExcelEntwurf>;
}

let attrappe: DokumentParser | null = null;

/** Nur fuer Tests. In der Auslieferung nie aufgerufen - Task 29 prueft das am Bundle. */
export function setzeParserFuerTest(p: DokumentParser | null): void {
  attrappe = p;
}

export async function ladeParser(): Promise<DokumentParser> {
  if (attrappe) return attrappe;
  // WOERTLICHES Literal, kein Umweg mehr ueber eine Konstante (Task 3, E2 des Parser-Plans): nur ein
  // statisch aufloesbarer dynamischer Import laesst Rollup/Vite ein eigenes, lazy geladenes Stueck
  // schneiden. Bis zu diesem Task stand hier `import(PARSER_MODUL_PFAD)` mit dem Pfad in einer
  // Konstante - der Grund dafuer ("`./index.ts` existiert nicht, ein woertliches Literal liesse Vites
  // `import-analysis`-Plugin das Ziel schon beim Transformieren dieser Datei aufloesen und das Laden
  // JEDER Datei abbrechen, die `naht.ts` transitiv importiert") gilt seit `./index.ts` im Baum liegt
  // NICHT MEHR. Mit der Konstante waere weiterhin KEINE Lazy-Grenze entstanden: Vite kann ein
  // nicht-literales Ziel nicht statisch aufloesen, `parsers/index.ts` laege dann in GAR KEINEM
  // Bundle-Stueck (siehe `test/reference/bundle.test.ts`, Abschnitt "Der Bündelwächter zieht mit").
  return (await import("./index.ts")).parser;
}
